import React, { lazy } from 'react';
import { Navigate, useParams } from 'react-router-dom';

// Eager imports — primary nav destinations, shared layouts, and anything
// rendered immediately after sign-in. Everything else is lazy-loaded below
// so the initial JS bundle stays small.
import { MainLayout } from '../components/organisms/MainLayout';
import { Home } from '../pages/Home/Home';
import { Parts } from '../pages/Part/Parts';
import { UpdatePart } from '../pages/Part/UpdatePart';
import { PickTaskList } from '../pages/PickTasks/PickTaskList';
import { Profile } from '../pages/Profile';
import { AddEditService } from '../pages/Services/AddEditService/AddEditService';
import { ServiceList } from '../pages/Services/ServiceList';
import { ServiceDetailRouter } from '../pages/Services/UpdateService/ServiceDetailRouter';
import { ShipmentList } from '../pages/Shipments/ShipmentList';
import { Structures } from '../pages/Structures';

// Lazy imports — specialty / admin / occasional flows. Grouped by area so
// webpack can still dedupe shared modules into per-area chunks.
const Users = lazy(() => import('../pages/Users').then((m) => ({ default: m.Users })));
const AddEditUser = lazy(() => import('../pages/Users').then((m) => ({ default: m.AddEditUser })));
const Roles = lazy(() => import('../pages/Roles').then((m) => ({ default: m.Roles })));
const AddEditRole = lazy(() => import('../pages/Roles').then((m) => ({ default: m.AddEditRole })));
const StructureTypes = lazy(() =>
  import('../pages/StructureTypes').then((m) => ({ default: m.StructureTypes }))
);
const AddEditStructureType = lazy(() =>
  import('../pages/StructureTypes').then((m) => ({ default: m.AddEditStructureType }))
);
const ClientAndContract = lazy(() =>
  import('../pages/ClientStructure/ClientAndContract').then((m) => ({
    default: m.ClientAndContract,
  }))
);
const AddEditClient = lazy(() =>
  import('../pages/ClientStructure/AddEditClient').then((m) => ({ default: m.AddEditClient }))
);
const ServiceStatuses = lazy(() =>
  import('../pages/ServiceStatuses').then((m) => ({ default: m.ServiceStatuses }))
);
const AddEditServiceStatus = lazy(() =>
  import('../pages/ServiceStatuses/AddEditServiceStatus/AddEditServiceStatus').then((m) => ({
    default: m.AddEditServiceStatus,
  }))
);
const ServiceTypes = lazy(() =>
  import('../pages/ServicesTypes/ServiceTypes').then((m) => ({ default: m.ServiceTypes }))
);
const AddEditServiceType = lazy(() =>
  import('../pages/ServicesTypes/AddEditServiceType').then((m) => ({
    default: m.AddEditServiceType,
  }))
);
const SLARuleList = lazy(() =>
  import('../pages/SLA/SLARules').then((m) => ({ default: m.SLARuleList }))
);
const PartTypes = lazy(() => import('../pages/PartType').then((m) => ({ default: m.PartTypes })));
const AddEditPartType = lazy(() =>
  import('../pages/PartType/AddEditPartType/AddEditPartType').then((m) => ({
    default: m.AddEditPartType,
  }))
);
const AllocationRuleList = lazy(() =>
  import('../pages/Allocations/AllocationRules').then((m) => ({ default: m.AllocationRuleList }))
);
const ReplenishmentConfigs = lazy(() =>
  import('../pages/ReplenishmentConfigs/ReplenishmentConfigs').then((m) => ({
    default: m.ReplenishmentConfigs,
  }))
);
const GeosocialRegions = lazy(() =>
  import('../pages/Geosocial/GeosocialRegions').then((m) => ({ default: m.GeosocialRegions }))
);
const GeosocialZones = lazy(() =>
  import('../pages/Geosocial/GeosocialZones').then((m) => ({ default: m.GeosocialZones }))
);
const SKURuleList = lazy(() =>
  import('../pages/SKU/SKURuleList').then((m) => ({ default: m.SKURuleList }))
);
const InteractionsRules = lazy(() =>
  import('../pages/Interactions/Rules/InteractionsRules').then((m) => ({
    default: m.InteractionsRules,
  }))
);
const InteractionsTemplate = lazy(() =>
  import('../pages/Interactions/Templates/InteractionTemplate').then((m) => ({
    default: m.InteractionsTemplate,
  }))
);
const NotificationRules = lazy(() =>
  import('../pages/Notifications/NotificationRules/NotificationRules').then((m) => ({
    default: m.NotificationRules,
  }))
);
const NotificationTemplates = lazy(() =>
  import('../pages/Notifications/NotificationTemplates/NotificationTemplates').then((m) => ({
    default: m.NotificationTemplates,
  }))
);
const ImportService = lazy(() =>
  import('../pages/Services/ImportServices').then((m) => ({ default: m.ImportService }))
);
const ImportStructure = lazy(() =>
  import('../pages/Structures/ImportStructures').then((m) => ({ default: m.ImportStructure }))
);
const AddEditStructureLocationWrapper = lazy(() =>
  import('../pages/Structures/AddEditStructureLocation/StructureLocationPageWrapper').then((m) => ({
    default: m.AddEditStructureLocationWrapper,
  }))
);
const CreateStructureWrapper = lazy(() =>
  import('../pages/Structures/CreateStructure').then((m) => ({
    default: m.CreateStructureWrapper,
  }))
);
const AttendanceReportList = lazy(() =>
  import('../pages/Reports/AttendanceReportList').then((m) => ({ default: m.AttendanceReportList }))
);
const ReplenishmentReportList = lazy(() =>
  import('../pages/Reports/ReplenishmentReportList').then((m) => ({
    default: m.ReplenishmentReportList,
  }))
);
const MyReportsList = lazy(() =>
  import('../pages/Reports/MyReportsList').then((m) => ({ default: m.MyReportsList }))
);
const AddEditStock = lazy(() =>
  import('../pages/Shipments/AddStock').then((m) => ({ default: m.AddEditStock }))
);
const CreateStock = lazy(() =>
  import('../pages/Shipments/AddStock/CreateStock').then((m) => ({ default: m.CreateStock }))
);
const EditShipment = lazy(() =>
  import('../pages/Shipments').then((m) => ({ default: m.EditShipment }))
);
const PickTaskDetail = lazy(() =>
  import('../pages/PickTasks/PickTaskDetail').then((m) => ({ default: m.PickTaskDetail }))
);
const Dispatch = lazy(() =>
  import('../pages/Shipments/Dispatch').then((m) => ({ default: m.Dispatch }))
);
const Return = lazy(() => import('../pages/Shipments/Return').then((m) => ({ default: m.Return })));
const IndividualShipmentReceive = lazy(() =>
  import('../pages/Shipments/StockReceivals/IndividualShipmentReceive').then((m) => ({
    default: m.IndividualShipmentReceive,
  }))
);
const BatchReceiveList = lazy(() =>
  import('../pages/Shipments/BatchReceiveList').then((m) => ({ default: m.BatchReceiveList }))
);
const AddEditBatchReceive = lazy(() =>
  import('../pages/Shipments/AddEditBatchReceive').then((m) => ({ default: m.AddEditBatchReceive }))
);
const CreateRepairJob = lazy(() =>
  import('../pages/Part/CreateRepairJob').then((m) => ({ default: m.CreateRepairJob }))
);
const BulkUpdate = lazy(() =>
  import('../pages/Part/BulkUpdate').then((m) => ({ default: m.BulkUpdate }))
);
const PartMovementList = lazy(() =>
  import('../pages/Part/MovementList').then((m) => ({ default: m.PartMovementList }))
);
const TLC = lazy(() => import('../pages/Part/TLC').then((m) => ({ default: m.TLC })));
const TLCTemplate = lazy(() =>
  import('../pages/Part/TLCTemplates').then((m) => ({ default: m.TLCTemplate }))
);
const AddEditTemplate = lazy(() =>
  import('../pages/Part/TLCTemplates/AddEditTemplate').then((m) => ({ default: m.AddEditTemplate }))
);
const NavigatorSupport = lazy(() =>
  import('../pages/NavigatorSupport/NavigatorSupport').then((m) => ({
    default: m.NavigatorSupport,
  }))
);
const JobsMap = lazy(() => import('../pages/Maps').then((m) => ({ default: m.JobsMap })));
const MobileLayout = lazy(() =>
  import('../pages/Mobile').then((m) => ({ default: m.MobileLayout }))
);
const MobileServices = lazy(() =>
  import('../pages/Mobile').then((m) => ({ default: m.MobileServices }))
);
const MobileServiceDetail = lazy(() =>
  import('../pages/Mobile').then((m) => ({ default: m.MobileServiceDetail }))
);
const SmartPayImport = lazy(() =>
  import('../pages/Tools/SmartPayImport').then((m) => ({ default: m.SmartPayImport }))
);
const AnzImport = lazy(() =>
  import('../pages/Tools/AnzImport').then((m) => ({ default: m.AnzImport }))
);
const FiservImport = lazy(() =>
  import('../pages/Tools/FiservImport').then((m) => ({ default: m.FiservImport }))
);
const Arc = lazy(() => import('../pages/Tools/Arc').then((m) => ({ default: m.Arc })));
const AmtekAnalytics = lazy(() =>
  import('../pages/Tools/AmtekAnalytics').then((m) => ({ default: m.AmtekAnalytics }))
);
const Wiki = lazy(() => import('../pages/Tools/Wiki').then((m) => ({ default: m.Wiki })));

/** Non-numeric /services/:id paths that should render ServiceList (pre-filtered reports) or AddEditService. */
const SERVICE_REPORT_PATHS = new Set([
  'myServices',
  'myDepot',
  'myZones',
  'myClosedSevenDays',
  'myClosedThirtyDays',
]);

/** Routes /services/:id — delegates based on the :id param value. */
const ServiceRouteSwitch: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  if (id === 'create') return <AddEditService />;
  if (id && SERVICE_REPORT_PATHS.has(id)) return <ServiceList key={id} />;
  return <ServiceDetailRouter />;
};

const routes = [];

export const home = {
  path: '/',
  element: <Home />,
};

// Client-customer users don't have a Home dashboard — `/` redirects them to
// the Jobs list, which is their effective landing page.
export const clientHome = {
  path: '/',
  element: <Navigate to="/services" replace />,
};

routes.push(
  {
    path: '/configure/users',
    element: <Users />,
  },
  {
    path: '/configure/users/:id',
    element: <AddEditUser />,
  },
  {
    path: '/configure/users/roles',
    element: <Roles />,
  },
  {
    path: '/configure/users/roles/:id',
    element: <AddEditRole />,
  },
  {
    path: '/configure/structure/types',
    element: <StructureTypes />,
  },
  {
    path: '/configure/structure/types/:id',
    element: <AddEditStructureType />,
  },
  {
    path: '/services',
    element: <ServiceList />,
  },
  {
    path: '/services/import',
    element: <ImportService />,
  },
  {
    path: '/services/:id',
    element: <ServiceRouteSwitch />,
  },
  {
    path: '/structure/search',
    element: <Structures />,
  },
  {
    path: '/structure/create',
    element: <CreateStructureWrapper />,
  },
  {
    path: '/structure/:id',
    element: <AddEditStructureLocationWrapper />,
  },
  {
    path: '/configure/structure/import',
    element: <ImportStructure />,
  },
  {
    path: '/configure/services/statuses',
    element: <ServiceStatuses />,
  },
  {
    path: '/configure/services/types',
    element: <ServiceTypes />,
  },
  {
    path: '/configure/clients',
    element: <ClientAndContract />,
  },
  {
    path: '/configure/services/types/:code',
    element: <AddEditServiceType />,
  },
  {
    path: '/configure/clients/:type/:id',
    element: <AddEditClient />,
  },
  {
    path: '/configure/services/statuses/:code',
    element: <AddEditServiceStatus />,
  },
  {
    path: '/configure/sla/rules',
    element: <SLARuleList />,
  },
  {
    path: '/configure/partTypes/:id',
    element: <AddEditPartType />,
  },
  {
    path: '/configure/partTypes',
    element: <PartTypes />,
  },
  {
    path: '/configure/allocation/rules',
    element: <AllocationRuleList />,
  },
  {
    path: '/parts/search',
    element: <Parts />,
  },
  {
    path: '/parts/createRepair',
    element: <CreateRepairJob />,
  },
  {
    path: '/parts/:id',
    element: <UpdatePart />,
  },
  {
    path: '/parts/bulkUpdate',
    element: <BulkUpdate />,
  },
  {
    path: '/parts/movements',
    element: <PartMovementList />,
  },
  {
    path: '/parts/tlc',
    element: <TLC />,
  },
  {
    path: '/configure/replenishment/config',
    element: <ReplenishmentConfigs />,
  },
  {
    path: '/configure/geosocial/regions',
    element: <GeosocialRegions />,
  },
  {
    path: '/configure/geosocial/zones',
    element: <GeosocialZones />,
  },
  {
    path: '/configure/sku/rules',
    element: <SKURuleList />,
  },
  {
    path: '/shipments',
    element: <ShipmentList />,
  },
  {
    path: '/shipments/stock/create',
    element: <CreateStock />,
  },
  {
    path: '/shipments/stock/:id',
    element: <AddEditStock />,
  },
  {
    path: '/shipments/dispatch/:id',
    element: <Dispatch />,
  },
  {
    path: '/shipments/return/:id',
    element: <Return />,
  },
  {
    path: '/shipments/:id',
    element: <EditShipment />,
  },
  {
    path: '/picktasks',
    element: <PickTaskList />,
  },
  {
    path: '/picktasks/:id',
    element: <PickTaskDetail />,
  },
  {
    path: '/reports/attendance',
    element: <AttendanceReportList />,
  },
  {
    path: '/reports/replenishment',
    element: <ReplenishmentReportList />,
  },
  {
    path: '/reports/myreports',
    element: <MyReportsList />,
  },
  {
    path: '/shipments/receive',
    element: <IndividualShipmentReceive />,
  },
  {
    path: '/shipments/receive/:id',
    element: <IndividualShipmentReceive />,
  },
  {
    path: '/configure/interactions/templates',
    element: <InteractionsTemplate />,
  },
  {
    path: '/configure/interactions/rules',
    element: <InteractionsRules />,
  },
  {
    path: '/configure/notifications/rules',
    element: <NotificationRules />,
  },
  {
    path: '/configure/notifications/templates',
    element: <NotificationTemplates />,
  },
  {
    path: '/shipments/batchReceive',
    element: <BatchReceiveList />,
  },
  {
    path: '/shipments/batchReceive/:id',
    element: <AddEditBatchReceive />,
  },
  {
    path: '/parts/tlcTemplates',
    element: <TLCTemplate />,
  },
  {
    path: '/parts/tlcTemplates/:id',
    element: <AddEditTemplate />,
  },
  {
    path: '/loader',
    element: <NavigatorSupport />,
  },
  {
    path: '/myProfile',
    element: <Profile />,
  },
  {
    path: '/services/myServices',
    element: <ServiceList />,
  },
  {
    path: '/services/myDepot',
    element: <ServiceList />,
  },
  {
    path: '/services/myZones',
    element: <ServiceList />,
  },
  {
    path: '/services/myClosedSevenDays',
    element: <ServiceList />,
  },
  {
    path: '/services/myClosedThirtyDays',
    element: <ServiceList />,
  },
  {
    path: '/maps',
    element: <JobsMap />,
  },
  {
    path: '/configure/structure/search',
    element: <Structures />,
  },
  {
    path: '/configure/structure/create',
    element: <CreateStructureWrapper isConfigPage />,
  },
  {
    path: '/configure/structure/:id',
    element: <AddEditStructureLocationWrapper isConfigPage />,
  },
  {
    path: '/tools/smartpay-import',
    element: <SmartPayImport />,
  },
  {
    path: '/tools/anz-import',
    element: <AnzImport />,
  },
  {
    path: '/tools/fiserv-import',
    element: <FiservImport />,
  },
  {
    path: '/tools/arc',
    element: <Arc />,
  },
  {
    path: '/tools/amtek-analytics',
    element: <AmtekAnalytics />,
  },
  {
    path: '/tools/wiki',
    element: <Wiki />,
  }
);

export const MainRoutes = {
  path: '/',
  element: <MainLayout />,
  children: routes,
};

// Mobile routes with separate layout
const mobileRoutes = [
  {
    path: 'services',
    element: <MobileServices />,
  },
  {
    path: 'services/:id',
    element: <MobileServiceDetail />,
  },
];

export const MobileRoutes = {
  path: '/mobile',
  element: <MobileLayout />,
  children: mobileRoutes,
};
